import type { WritableComputedRef } from 'vue'
import { defineStore } from 'pinia'
import type { CustomStoreImplementationOptions, KaElementClickEvent, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { initAliveStoreIds, kapixContext, navigateTo, openUrl, remove, scrollInfo } from 'kapix-components-vue3'
import histoireStoreCustom from './store.custom'
import HautDePage from '~/menus/HautDePage/index.vue'
import MenuEntreprise from '~/menus/MenuEntreprise/index.vue'
import MenuExpert from '~/menus/MenuExpert/index.vue'
import MenuFormations from '~/menus/MenuFormations/index.vue'
import { $translate } from '~/modules/i18n'

const storeName = 'histoire'
const customImplement: CustomStoreImplementationOptions = histoireStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function histoireFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        // Page info,
        $title: $translate('notre-histoire.title', 'L\'histoire de Matchers, start-up Montpellièraine '),
        $description: $translate('notre-histoire.description', 'Fondé en mars 2019 par Jean-Marie Dupré et Stéphane Silvi, Matchers lève 2 M€ en fin 2023. Retour sur la création de cette entreprise, révolutionnaire de la formation professionnelle pour les petites entreprises. '),
        $publishedTime: new Date('2023-03-22T15:47:38.288Z'),
        $modifiedTime: new Date('2023-11-07T15:41:39.720Z'),
        // Constants,
        $constants: { mars2020Matchers: 'Mars 2020, Matchers lance la première version de sa plateforme dans un contexte où la vente de services aux entreprises était pénalisée. Jean-Marie et Stéphane identifient alors tous les freins, les contraintes mais aussi les réels besoins des entreprises. Face à cette France qui tourne au ralenti, ils basculent pour tester leur modèle en format associatif.<br> <br>Leurs experts sont, dans la grande majorité, au chômage partiel et ont donc du temps. Matchers proposent donc à ces professionnels d\'accompagner gratuitement de petites entreprises pour préparer la reprise. Ils lancent le dispositif "Solidarité PME" et une centaine répondent à l\'appel pour devenir bénévoles. ' },
        // Data,
        $data: {
          version: '1.0.0' as Nullable<string>,
          ongletAPropos: '<p>À propos</p>'
        },
        $inputs: { saisieDeTexte: undefined as Nullable<string> },
        $info: { notreHistoire: scrollInfo() },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      /* @ts-ignore: to allow override in your custom file */
      saisieDeTexte (): WritableComputedRef<Nullable<string>> {
        return computed({
          get: () => this.$inputs.saisieDeTexte,
          set: (value) => {
            this.$inputs.saisieDeTexte = value
          }
        })
      },
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      async logoDeLentrepriseClick () {
        await navigateTo({ path: '/' })
      },
      /* @ts-ignore: to allow override in your custom file */
      headerOngletClick (event: KaElementClickEvent) {
        event.vm?.showTooltip(MenuEntreprise)
      },
      /* @ts-ignore: to allow override in your custom file */
      element1Click (event: KaElementClickEvent) {
        event.vm?.showTooltip(MenuExpert)
      },
      /* @ts-ignore: to allow override in your custom file */
      element2Click (event: KaElementClickEvent) {
        event.vm?.showTooltip(MenuFormations)
      },
      /* @ts-ignore: to allow override in your custom file */
      async ongletAProposClick () {
        await navigateTo({ name: 'Infos' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async contactClick () {
        await navigateTo({ name: 'Contact' })
      },
      /* @ts-ignore: to allow override in your custom file */
      menuBurgerClickOnXs (event: KaElementClickEvent) {
        event.vm?.showTooltip(HautDePage)
      },
      /* @ts-ignore: to allow override in your custom file */
      menuBurgerClickOnXsWhenSelected (event: KaElementClickEvent) {
        event.vm?.closeAllTooltips()
      },
      /* @ts-ignore: to allow override in your custom file */
      leCommuniqueDePresseClick () {
        openUrl('https://drive.google.com/file/d/1ozlrZiibXOmMvBqFlbxPf5xIr7Wa957w/view?usp=drive_link', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      leKitDeCommunicationClick () {
        openUrl('https://drive.google.com/drive/folders/1ELGsUtJjpatC_GXuYjMNUa8TssSImu-5?usp=drive_link', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      presentationDesCoFondateursClick () {
        openUrl('https://docs.google.com/presentation/d/1rSy8wnFrlVdW4oQD1EDq7fqpHEZAm5ip-uh8C9MLysA/edit?usp=sharing', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      boutonClick () {
        openUrl('https://meetings.hubspot.com/claire-bachini', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      async donneesPersonnellesClick () {
        await navigateTo({ name: 'donnees-personnelles' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async conditionsGeneralesClick () {
        await navigateTo({ name: 'Cgv' })
      },
      /* @ts-ignore: to allow override in your custom file */
      async mentionsLegalesClick () {
        await navigateTo({ name: 'mentions-legales' })
      },
      /* @ts-ignore: to allow override in your custom file */
      icon2Click () {
        openUrl('https://www.linkedin.com/company/linkedin-com-company-matchers/', undefined)
      },
      /* @ts-ignore: to allow override in your custom file */
      icon3Click () {
        openUrl('https://www.facebook.com/matchersfr', undefined)
      },
      /* @ts-ignore: to allow override in your custom file */
      soumettreClick () {
        /* @ts-ignore: custom code */
        this.handleSubmitNewsLetter()
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return histoireFactory(storeId)()
}

export const histoireRaw = histoireFactory()
export const histoire = () => histoireRaw() as MergeCustomStore<typeof histoireStoreCustom.instance>
